<template>
  <div class="two-factor-setup-wrapper">
    <!-- Header -->
    <div class="text-center mb-4" v-if="showHeader">
      <i class="ri-shield-star-line display-4 text-warning"></i>
      <h3 class="mt-3 text-warning">{{ headerTitle }}</h3>
      <p class="text-muted">{{ headerMessage }}</p>
      <div class="alert alert-warning" v-if="showAlert">
        <i class="ri-information-line mr-2"></i>
        {{ alertMessage }}
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading && !qrCodeDataUrl" class="two-factor-loading">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Configurando...</span>
      </div>
      <div class="loading-text mt-3">Preparando configuración 2FA...</div>
    </div>

    <!-- Setup Process -->
    <div v-else class="two-factor-steps">
      <!-- Step Indicator -->
      <TwoFactorStepIndicator :current-step="setupStep" />

      <!-- Step 1: QR Code -->
      <div v-if="setupStep === 1" class="step active">
        <TwoFactorQRStep
          :qr-code-url="qrCodeUrl"
          :qr-code-data-url="qrCodeDataUrl"
          :secret-key="secretKey"
          :backup-codes="backupCodes"
          :loading="loading"
          :cancel-label="cancelLabel"
          @copy-secret="handleCopySecret"
          @copy-codes="handleCopyBackupCodes"
          @cancel="handleCancel"
          @continue="continueToStep2"
        />
      </div>

      <!-- Step 2: Verification -->
      <div v-if="setupStep === 2" class="step active">
        <TwoFactorVerificationStep
          :code="confirmCode"
          @update:code="confirmCode = $event"
          :loading="loading"
          :confirm-text="confirmText"
          :loading-text="loadingText"
          @back="backToStep1"
          @confirm="handleConfirm"
        />
      </div>
    </div>

    <!-- Recommended Apps -->
    <div class="mt-5 pt-4 border-top" v-if="showRecommendedApps">
      <h6 class="text-muted mb-3">Aplicaciones recomendadas:</h6>
      <div class="row text-center">
        <div class="col-4">
          <div class="p-2">
            <i class="ri-smartphone-line font-size-18 text-primary mb-1"></i>
            <div class="small">Google<br>Authenticator</div>
          </div>
        </div>
        <div class="col-4">
          <div class="p-2">
            <i class="ri-shield-keyhole-line font-size-18 text-success mb-1"></i>
            <div class="small">Authy</div>
          </div>
        </div>
        <div class="col-4">
          <div class="p-2">
            <i class="ri-smartphone-line font-size-18 text-info mb-1"></i>
            <div class="small">Microsoft<br>Authenticator</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import TwoFactorStepIndicator from './TwoFactorStepIndicator.vue';
import TwoFactorQRStep from './TwoFactorQRStep.vue';
import TwoFactorVerificationStep from './TwoFactorVerificationStep.vue';
import { notificationMethods } from "@/state/helpers";
import QRCode from 'qrcode';
import {
  authMethods
} from "@/state/helpers";

export default {
  name: 'TwoFactorSetupWrapper',
  components: {
    TwoFactorStepIndicator,
    TwoFactorQRStep,
    TwoFactorVerificationStep
  },
  props: {
    // UI Configuration
    showHeader: {
      type: Boolean,
      default: true
    },
    showAlert: {
      type: Boolean,
      default: true
    },
    showRecommendedApps: {
      type: Boolean,
      default: true
    },
    
    // Text customization
    headerTitle: {
      type: String,
      default: 'Configuración 2FA Requerida'
    },
    headerMessage: {
      type: String,
      default: 'Tu administrador requiere que configures la autenticación en dos pasos antes de continuar.'
    },
    alertMessage: {
      type: String,
      default: 'Debes configurar la autenticación en dos pasos para continuar.'
    },
    cancelLabel: {
      type: String,
      default: 'Volver al login'
    },
    confirmText: {
      type: String,
      default: 'Completar configuración'
    },
    loadingText: {
      type: String,
      default: 'Configurando...'
    },
    
    // Auto start setup
    autoStart: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      // Setup process state
      loading: false,
      setupStep: 1,
      qrCodeUrl: '',
      qrCodeDataUrl: '',
      secretKey: '',
      backupCodes: [],
      
      // Form data
      confirmCode: ''
    };
  },
  async mounted() {
    if (this.autoStart) {
      await this.initializeSetup();
    }
  },
  methods: {
    ...notificationMethods,
    ...authMethods,
    
    async initializeSetup() {
      try {
        await this.startSetup();
      } catch (error) {
        console.error('Error initializing 2FA setup:', error);
        this.error(error.message || 'Error al inicializar la configuración de 2FA');
        this.$emit('setup-error', error);
      }
    },

    // Start 2FA setup process
    async startSetup() {
      try {
        this.loading = true;
        const response = await this.enable2FA();
        
        console.log('2FA Setup response:', response);
        this.qrCodeUrl = response.qr_code_url;
        this.secretKey = response.secret_key;
        this.backupCodes = response.backup_codes;
        
        // Generate QR code from URL
        await this.generateQRCode();
        
        return response;
      } catch (error) {
        console.error('Error starting 2FA setup:', error);
        throw new Error('Error al inicializar la configuración de 2FA');
      } finally {
        this.loading = false;
      }
    },

    // Generate QR code from URL
    async generateQRCode() {
      if (!this.qrCodeUrl) return;
      
      try {
        this.qrCodeDataUrl = await QRCode.toDataURL(this.qrCodeUrl, {
          width: 200,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          }
        });
      } catch (error) {
        console.error('Error generating QR code:', error);
        throw new Error('Error al generar el código QR');
      }
    },

    // Confirm 2FA setup with verification code
    async confirmSetup(code) {
      if (!code || code.length !== 6) {
        throw new Error('Ingresa un código de 6 dígitos');
      }
      
      try {
        this.loading = true;
        const result = await this.confirm2FA({ code });
        
        // Reset setup state
        this.resetSetupData();
        
        return result;
      } catch (error) {
        console.error('Error confirming 2FA:', error);
        throw new Error('Código inválido. Verifica tu aplicación autenticadora.');
      } finally {
        this.loading = false;
      }
    },

    // Reset setup data
    resetSetupData() {
      this.setupStep = 1;
      this.qrCodeUrl = '';
      this.qrCodeDataUrl = '';
      this.secretKey = '';
      this.backupCodes = [];
      this.confirmCode = '';
    },

    // Navigation helpers
    continueToStep2() {
      if (this.setupStep === 1) {
        this.setupStep = 2;
      }
    },

    backToStep1() {
      if (this.setupStep === 2) {
        this.setupStep = 1;
      }
    },

    // Copy operations
    async copySecretKey() {
      try {
        await navigator.clipboard.writeText(this.secretKey);
        return { success: true, message: 'Clave secreta copiada al portapapeles' };
      } catch (error) {
        console.error('Error copying secret key:', error);
        return { success: false, message: 'No se pudo copiar la clave secreta' };
      }
    },

    async copyBackupCodes() {
      const codesText = this.backupCodes.join('\n');
      try {
        await navigator.clipboard.writeText(codesText);
        return { success: true, message: 'Códigos copiados al portapapeles' };
      } catch (error) {
        console.error('Error copying to clipboard:', error);
        return { success: false, message: 'No se pudieron copiar los códigos' };
      }
    },

    async handleCopySecret() {
      try {
        const result = await this.copySecretKey();
        if (result.success) {
          this.success(result.message);
        } else {
          this.error(result.message);
        }
      } catch (error) {
        this.error('Error al copiar la clave secreta');
      }
    },

    async handleCopyBackupCodes() {
      try {
        const result = await this.copyBackupCodes();
        if (result.success) {
          this.success(result.message);
        } else {
          this.error(result.message);
        }
      } catch (error) {
        this.error('Error al copiar los códigos de recuperación');
      }
    },

    handleCancel() {
      this.resetSetupData();
      this.$emit('setup-cancelled');
    },

    async handleConfirm() {
      try {
        const result = await this.confirmSetup(this.confirmCode);
        this.success('¡2FA configurado exitosamente!');
        this.$emit('setup-completed', result);
      } catch (error) {
        console.error('Error confirming 2FA:', error);
        this.error(error.message || 'Error al confirmar la configuración 2FA');
        this.$emit('setup-error', error);
      }
    }
  }
};
</script>

<style scoped>
.two-factor-setup-wrapper {
  max-width: 500px;
  margin: 0 auto;
  padding: 2rem;
}

.step {
  animation: fadeInUp 0.3s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(1rem);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style> 